import { isDev } from '@/components/utils/config'
import axios from 'axios'

let api
if (isDev) {
  if (process.env.NODE_ENV === 'local') {
    api = 'https://localhost:3000'
  } else {
    api = 'https://spotwork-server-dev-gqs5kyb4aa-an.a.run.app'
    // api = 'https://localhost:3000'
  }
} else {
  api = 'https://spotwork-server-prod-krwbx2bdoq-an.a.run.app'
}

const getHeaders = (token) => {
  if (token) {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  }
  return {
    'Content-Type': 'application/json'
  }
}

class ApiError extends Error {
  constructor(message, code, data = null) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.data = data
  }
}

const postRequest = async (formData, endpoint, token = '') => {
  if (!token) {
    const err = new ApiError('認証トークンがありません', 'auth-required')
    err.httpStatus = 401
    throw err
  }
  const result = await axios
    .post(`${api}/${endpoint}`, formData, {
      headers: getHeaders(token)
    })
    .then((res) => res.data)
    .catch((error) => {
      if (error.response && error.response.data) {
        const err = new ApiError(
          error.response.data.message || 'APIエラーが発生しました',
          error.response.data.code || 'unknown-error',
          error.response.data
        )
        err.httpStatus = error.response.status
        throw err
      }
      const err = new ApiError(
        error.message || '予期せぬエラーが発生しました',
        'unknown-error'
      )
      err.httpStatus = error.response?.status
      throw err
    })

  return result
}

const getRequest = async (endpoint, token = '') => {
  if (!token) {
    const err = new ApiError('認証トークンがありません', 'auth-required')
    err.httpStatus = 401
    throw err
  }
  const result = await axios
    .get(`${api}/${endpoint}`, {
      headers: getHeaders(token)
    })
    .then((res) => res.data)
    .catch((error) => {
      if (error.response && error.response.data) {
        const err = new ApiError(
          error.response.data.message || 'APIエラーが発生しました',
          error.response.data.code || 'unknown-error',
          error.response.data
        )
        err.httpStatus = error.response.status
        throw err
      }
      const err = new ApiError(
        error.message || '予期せぬエラーが発生しました',
        'unknown-error'
      )
      err.httpStatus = error.response?.status
      throw err
    })

  return result
}

const receiveLinePayDeposit = async (token, uid, amount) => {
  const endpoint = 'deposit/line/send'
  const body = {
    uid: uid,
    amount: amount
  }
  return await postRequest(body, endpoint, token)
}

const receiveGMOTotalDeposit = async (token, uid, amount) => {
  const endpoint = 'deposit/gmo/total/send'
  const body = {
    uid: uid,
    amount: amount
  }
  return await postRequest(body, endpoint, token)
}

const recognizeEkyc = async (token, uid, frontImage, backImage = null) => {
  const endpoint = 'ekyc/recognize'
  const body = {
    uid: uid,
    frontImage: frontImage
  }
  if (backImage) body.backImage = backImage
  return await postRequest(body, endpoint, token)
}
const checkAndSaveEkycDataEkyc = async (
  token,
  uid,
  name,
  address,
  birthday,
  inputName,
  inputAddress,
  inputBirthday,
  frontImage,
  backImage = null,
  remarks = null
) => {
  const endpoint = 'ekyc/checkAndSaveEkycData'
  const body = {
    uid: uid,
    name: name,
    address: address,
    birthday: birthday,
    inputName: inputName,
    inputAddress: inputAddress,
    inputBirthday: inputBirthday,
    frontImage: frontImage
  }
  if (backImage) body.backImage = backImage
  if (remarks) body.remarks = remarks
  return await postRequest(body, endpoint, token)
}

const fetchEkycName = async (token, uid) => {
  const endpoint = 'ekyc/fetch/name'
  const body = {
    uid: uid
  }
  return await postRequest(body, endpoint, token)
}

const checkLinePayExists = async (token, uid, linePay) => {
  const endpoint = 'user/checkLinePayExists'
  const body = {
    uid: uid,
    linePay: linePay
  }
  return await postRequest(body, endpoint, token)
}
/**
 * LINE IDの重複チェックを行います
 *
 * @param {string} token
 * @param {string} lineid
 * @returns {Promise<{
 *   status: "success",
 *   isDuplication: boolean
 *  }>
 * } true:重複あり false:重複なし
 */
const checkLineIdDuplication = async (token, uid, lineId) => {
  const endpoint = 'user/checkLineIdDuplication'
  const body = {
    uid: uid,
    lineId: lineId
  }
  return await postRequest(body, endpoint, token)
}

/**
 * 電話番号の重複チェックを行います
 * @param {string} token
 * @param {string} telephoneNumber
 * @returns {Promise<{
 *   status: "success",
 *   isDuplication: boolean
 *  }>
 * } true:重複あり false:重複なし
 */
const checkTelephoneNumberDuplication = async (token, uid, telephoneNumber) => {
  const endpoint = 'user/checkTelephoneNumberDuplication'
  const body = {
    uid: uid,
    telephoneNumber: telephoneNumber
  }
  return await postRequest(body, endpoint, token)
}

const checkIsTextFieldDataDuplicated = async (
  token,
  uid,
  workId,
  elementId,
  value
) => {
  const endpoint = 'work/checkIsTextFieldDataDuplicated'
  const body = {
    uid: uid,
    workId: workId,
    elementId: String(elementId),
    value: value
  }
  return await postRequest(body, endpoint, token)
}

const checkIsTextFieldDataExcludedNGList = async (
  token,
  uid,
  workId,
  elementId,
  value
) => {
  const endpoint = 'work/checkIsTextFieldDataExcludedNGList'
  const body = {
    uid: uid,
    workId: workId,
    elementId: String(elementId),
    value: value
  }
  return await postRequest(body, endpoint, token)
}

// Add new offer-related API functions
const acceptOffer = async (params, token = '') => {
  const endpoint = 'offer/accept'
  const body = {
    offerId: params.offerId,
    companyId: params.companyId,
    userId: params.userId,
    userAccessibleUserId: params.userAccessibleUserId,
    scheduledDate: params.scheduledDate?.toISOString(),
    orderId: params.orderId,
    currentLocation: params.currentLocation
  }
  return await postRequest(body, endpoint, token)
}

const rejectOffer = async (
  offerId,
  orderId,
  companyId,
  userId,
  userAccessibleUserId,
  token = ''
) => {
  const endpoint = 'offer/reject'
  const body = {
    offerId,
    orderId,
    companyId,
    userId,
    userAccessibleUserId
  }
  return await postRequest(body, endpoint, token)
}

const getPendingOffers = async (token) => {
  return await getRequest('offer/pending', token)
}

const updateEmail = async (token, uid, oldEmail, newEmail) => {
  const endpoint = 'userAuth/updateEmail'
  const body = {
    uid: uid,
    oldEmail: oldEmail,
    newEmail: newEmail
  }
  return await postRequest(body, endpoint, token)
}

const verifyEmail = async (token, uid, code) => {
  const endpoint = 'userAuth/verifyEmail'
  const body = {
    uid: uid,
    code: code
  }
  return await postRequest(body, endpoint, token)
}

const getBankAccount = async (token, uid) => {
  const endpoint = 'user/gmoBankAccount'
  const body = {
    uid: uid
  }
  return await postRequest(body, endpoint, token)
}

const deleteBankAccount = async (token, uid) => {
  const endpoint = 'deposit/gmo/total/deleteBank'
  const body = {
    uid: uid
  }
  return await postRequest(body, endpoint, token)
}

const submitSeminarCode = async (
  token,
  uid,
  companyId,
  seminarId,
  passcode
) => {
  const endpoint = 'businessSeminar/submitCode'
  const body = {
    uid: uid,
    companyId: companyId,
    seminarId: seminarId,
    passcode: passcode
  }
  return await postRequest(body, endpoint, token)
}

/**
 * Firestore BATTERY 新規則 doc の登録（ModuleBatteryRegister 用）
 * Authorization Bearer と body.uid の両方が必要（firebaseSelfAuth）
 * @param {string} token - Firebase ID トークン
 * @param {string} uid - 認証ユーザー UID（body.uid = トークン uid）
 * @param {string} serial
 */
const registerBatteryDoc = async (token, uid, serial) => {
  return await postRequest(
    { uid: String(uid || '').trim(), serial: String(serial || '').trim() },
    'chargeSpot/battery/register',
    token
  )
}

/**
 * Firestore BATTERY の userId 等を更新（一覧同期・解放用）
 * Authorization Bearer と body.uid の両方が必要（firebaseSelfAuth）
 * @param {string} token - Firebase ID トークン
 * @param {string} uid - 認証ユーザー UID（body.uid = トークン uid）
 * @param {string} serial
 * @param {object} fields - userId / status 等（body.uid とは別。対象バッテリーの所有者）
 */
const updateBatteryUser = async (token, uid, serial, fields) => {
  return await postRequest(
    {
      uid: String(uid || '').trim(),
      serial: String(serial || '').trim(),
      ...fields
    },
    'chargeSpot/battery/updateBatteryUser',
    token
  )
}

/**
 * spotJobs の保有一覧（正本）を元に Firestore `BATTERY/{serial}` をまとめて同期する
 * `updatedAt` は spotJobs の updateTime（取り出し日）を優先して送る
 * @param {string} token
 * @param {string} uid
 * @param {{serial: string, updatedAt?: string}[]} batteries
 */
const syncHoldingBatteries = async (token, uid, batteries) => {
  return await postRequest(
    {
      uid: String(uid || '').trim(),
      batteries: Array.isArray(batteries) ? batteries : []
    },
    'chargeSpot/battery/syncHolding',
    token
  )
}

/**
 * API に無いが DB に残っている serial をまとめて解放する（userId: null 等）
 * @param {string} token
 * @param {string} uid
 * @param {string[]} serials
 */
const releaseMissingBatteries = async (token, uid, serials) => {
  return await postRequest(
    {
      uid: String(uid || '').trim(),
      serials: Array.isArray(serials) ? serials : []
    },
    'chargeSpot/battery/releaseMissing',
    token
  )
}

const submitLicenseTest = async (
  token,
  uid,
  companyId,
  licenseId,
  submissionId
) => {
  const endpoint = 'businessLicense/submitLicenseTest'
  const body = {
    userId: uid,
    companyId: companyId,
    licenseId: licenseId,
    submissionId: submissionId
  }
  return await postRequest(body, endpoint, token)
}

const getWorkerOrdersNear = async (
  token,
  { lat, lng, radiusInM = 1000, limit = 200 }
) => {
  const params = new URLSearchParams({
    lat: String(lat),
    lng: String(lng)
  })
  if (Number.isFinite(Number(radiusInM)) && Number(radiusInM) > 0) {
    params.set('radiusInM', String(radiusInM))
  }
  if (Number.isFinite(Number(limit)) && Number(limit) > 0) {
    params.set('limit', String(Math.floor(Number(limit))))
  }
  return await getRequest(`worker/orders/near?${params.toString()}`, token)
}

const getLookingForOrders = async (token, { lat, lng } = {}) => {
  const params = new URLSearchParams()
  if (lat != null) params.set('lat', String(lat))
  if (lng != null) params.set('lng', String(lng))
  const query = params.toString()
  const endpoint = query
    ? `worker/orders/looking-for-order?${query}`
    : 'worker/orders/looking-for-order'
  return await getRequest(endpoint, token)
}

const getWorkerOrderDetail = async (token, orderId) => {
  return await getRequest(`worker/orders/${orderId}`, token)
}

const getWorkerSpotOrderDetail = async (token, orderId) => {
  return await getRequest(`worker/orders/${orderId}/spot-detail`, token)
}

const getWorkerOrderCancelWaits = async (token) => {
  return await getRequest('worker/orders/cancel-waits', token)
}

const reserveWorkerOrder = async (
  token,
  orderId,
  { currentLocation, scheduledDate = null, reservationDate = null } = {}
) => {
  const body = {}
  if (
    currentLocation &&
    currentLocation.lat != null &&
    currentLocation.lng != null
  ) {
    body.currentLocation = {
      lat: currentLocation.lat,
      lng: currentLocation.lng
    }
  }
  if (scheduledDate) {
    body.scheduledDate =
      scheduledDate instanceof Date
        ? scheduledDate.toISOString()
        : scheduledDate
  }
  if (reservationDate) {
    body.reservationDate =
      reservationDate instanceof Date
        ? reservationDate.toISOString()
        : reservationDate
  }
  return await postRequest(body, `worker/orders/${orderId}/reserve`, token)
}

const registerWorkerOrderCancelWait = async (
  token,
  orderId,
  { currentLocation } = {}
) => {
  const body = {}
  if (
    currentLocation &&
    currentLocation.lat != null &&
    currentLocation.lng != null
  ) {
    body.currentLocation = {
      lat: currentLocation.lat,
      lng: currentLocation.lng
    }
  }
  return await postRequest(body, `worker/orders/${orderId}/cancel-wait`, token)
}

const removeWorkerOrderCancelWait = async (token, orderId) => {
  return await postRequest(
    {},
    `worker/orders/${orderId}/cancel-wait/remove`,
    token
  )
}

const registerPreparingInterest = async (token, orderId) => {
  return await postRequest(
    {},
    `worker/orders/${orderId}/preparing-interest`,
    token
  )
}

const removePreparingInterest = async (token, orderId) => {
  return await postRequest(
    {},
    `worker/orders/${orderId}/preparing-interest/remove`,
    token
  )
}

const decideWorkerOrderSchedule = async (token, orderId, scheduledDate) => {
  const body = {
    scheduledDate:
      scheduledDate instanceof Date
        ? scheduledDate.toISOString()
        : scheduledDate
  }
  return await postRequest(
    body,
    `worker/orders/${orderId}/schedule/decide`,
    token
  )
}

const rejectWorkerOrderSchedule = async (token, orderId) => {
  return await postRequest(
    {},
    `worker/orders/${orderId}/schedule/reject`,
    token
  )
}

const reviewWorkerOrder = async (token, orderId, { workId, score, text }) => {
  return await postRequest(
    { workId, score, text },
    `worker/orders/${orderId}/review`,
    token
  )
}

const applyWorkerOrder = async (
  token,
  orderId,
  { scheduledDate = null } = {}
) => {
  const body = {}
  if (scheduledDate) {
    body.scheduledDate =
      scheduledDate instanceof Date
        ? scheduledDate.toISOString()
        : scheduledDate
  }
  return await postRequest(body, `worker/orders/${orderId}/apply`, token)
}

const removeApplyWorkerOrder = async (token, orderId) => {
  return await postRequest({}, `worker/orders/${orderId}/apply/remove`, token)
}

const completeCustomWorkerOrder = async (token, orderId, { workId }) => {
  return await postRequest(
    { workId },
    `worker/orders/${orderId}/complete-custom`,
    token
  )
}

const completeLookingForWorkerOrder = async (token, orderId, { workId }) => {
  return await postRequest(
    { workId },
    `worker/orders/${orderId}/complete-looking-for-order`,
    token
  )
}

const completeShopWorkerOrder = async (token, orderId, { workId }) => {
  return await postRequest(
    { workId },
    `worker/orders/${orderId}/complete-shop`,
    token
  )
}

const removeMatchedApplyWorkerOrder = async (token, orderId) => {
  return await postRequest(
    {},
    `worker/orders/${orderId}/apply/remove-matched`,
    token
  )
}

const getCurrentApplyWorkerOrder = async (token, orderId) => {
  return await getRequest(`worker/orders/${orderId}/apply/current`, token)
}

const getCurrentMatchedApplyWorkerOrder = async (token, orderId) => {
  return await getRequest(
    `worker/orders/${orderId}/apply/current-matched`,
    token
  )
}

const getMyApplyingWorkerOrders = async (token) => {
  return await getRequest('worker/orders/applications/my', token)
}

// const incrementRecruitRequirementReadCount = async (
//   token,
//   { companyId, recruitRequirementId, amount = 1, accessibleUserId = undefined }
// ) => {
//   const endpoint = 'recruitRequirement/incrementReadCount'
//   const body = {
//     companyId,
//     recruitRequirementId,
//     amount,
//     accessibleUserId
//   }
//   console.log('DEBUG: token', token)
//   console.log('DEBUG: body', body)
//   if (accessibleUserId) body.accessibleUserId = accessibleUserId
//   return await postRequest(body, endpoint, token)
// }

const spotworkAPI = {
  receiveLinePayDeposit,
  receiveGMOTotalDeposit,
  recognizeEkyc,
  checkAndSaveEkycDataEkyc,
  fetchEkycName,
  checkLinePayExists,
  checkLineIdDuplication,
  checkTelephoneNumberDuplication,
  checkIsTextFieldDataDuplicated,
  checkIsTextFieldDataExcludedNGList,
  acceptOffer,
  rejectOffer,
  getPendingOffers,
  updateEmail,
  verifyEmail,
  getBankAccount,
  deleteBankAccount,
  submitSeminarCode,
  submitLicenseTest,
  getWorkerOrdersNear,
  getLookingForOrders,
  getWorkerOrderDetail,
  getWorkerSpotOrderDetail,
  getWorkerOrderCancelWaits,
  reserveWorkerOrder,
  registerWorkerOrderCancelWait,
  removeWorkerOrderCancelWait,
  registerPreparingInterest,
  removePreparingInterest,
  decideWorkerOrderSchedule,
  rejectWorkerOrderSchedule,
  reviewWorkerOrder,
  applyWorkerOrder,
  removeApplyWorkerOrder,
  completeCustomWorkerOrder,
  completeLookingForWorkerOrder,
  completeShopWorkerOrder,
  removeMatchedApplyWorkerOrder,
  getCurrentApplyWorkerOrder,
  getCurrentMatchedApplyWorkerOrder,
  getMyApplyingWorkerOrders,
  registerBatteryDoc,
  updateBatteryUser,
  syncHoldingBatteries,
  releaseMissingBatteries
  // incrementRecruitRequirementReadCount
}

export default spotworkAPI
